import React from "react"
import { useTranslation } from 'react-i18next';
import { Analytics } from "@vercel/analytics/react"
import { Header, Footer } from "./LandingPage"
import { FileDown, Compass, ListChecks, Award, Calculator, LucideIcon, Check, X } from "lucide-react"
import GradientBackground from './sections/GradientBackground'
import SEO from './common/SEO'

interface ComparisonRow {
  key: string;
  free: boolean;
  pro: boolean;
}

const comparisonRows: ComparisonRow[] = [
  { key: "manualLogging", free: true, pro: true },
  { key: "diveSiteExplore", free: true, pro: true },
  { key: "communityFeed", free: true, pro: true },
  { key: "uddfExport", free: true, pro: true },
  { key: "bluetoothSync", free: false, pro: true },
  { key: "diveTools", free: false, pro: true },
  { key: "advancedStats", free: false, pro: true },
  { key: "gearKits", free: false, pro: true },
  { key: "csvExport", free: true, pro: true },
  { key: "pdfExport", free: false, pro: true },
];

const ComparisonMark: React.FC<{ included: boolean; accent?: boolean }> = ({ included, accent = false }) => (
  included ? (
    <span className={`inline-flex w-6 h-6 items-center justify-center rounded-full ${accent ? 'bg-[#1F80FF]/15' : 'bg-[#5C9C7F]/15'}`}>
      <Check className={`w-3.5 h-3.5 ${accent ? 'text-[#1F80FF]' : 'text-[#5C9C7F]'}`} strokeWidth={3} />
    </span>
  ) : (
    <span className="inline-flex w-6 h-6 items-center justify-center rounded-full bg-black/5">
      <X className="w-3.5 h-3.5 text-muted-foreground/50" strokeWidth={3} />
    </span>
  )
);

const FreeVsProSection: React.FC = () => {
  const { t } = useTranslation();
  return (
    <section className="container py-12 sm:py-16 lg:py-20 z-20">
      <div className="text-center mb-10 sm:mb-14">
        <h2 className="text-2xl sm:text-3xl md:text-4xl font-black tracking-tight">{t('pro.comparison.title')}</h2>
        <p className="mt-4 text-base sm:text-lg text-muted-foreground">
          {t('pro.comparison.subtitle')}
        </p>
      </div>

      <div className="max-w-[720px] mx-auto glass-card rounded-[1.75rem] overflow-hidden">
        <table className="w-full border-collapse">
          <thead>
            <tr className="border-b border-black/5">
              <th className="text-left font-semibold text-sm text-muted-foreground py-5 pl-6 sm:pl-8 pr-4">{t('pro.comparison.headers.feature')}</th>
              <th className="text-center font-bold text-sm py-5 px-4 w-24">{t('pro.comparison.headers.free')}</th>
              <th className="text-center py-5 px-4 w-28 sm:pr-8">
                <span className="inline-flex items-center gap-1.5 font-bold text-sm text-[#1F80FF]">
                  {t('pro.comparison.headers.pro')}
                  <span className="bg-[#1F80FF]/15 text-[#1F80FF] text-[10px] font-bold px-1.5 py-0.5 rounded-full">★</span>
                </span>
              </th>
            </tr>
          </thead>
          <tbody>
            {comparisonRows.map((row, i) => (
              <tr key={row.key} className={i !== comparisonRows.length - 1 ? "border-b border-black/5" : ""}>
                <td className="py-3.5 pl-6 sm:pl-8 pr-4 text-sm">{t(`pro.comparison.rows.${row.key}`)}</td>
                <td className="py-3.5 px-4 text-center">
                  <ComparisonMark included={row.free} />
                </td>
                <td className="py-3.5 px-4 sm:pr-8 text-center bg-[#1F80FF]/[0.03]">
                  <ComparisonMark included={row.pro} accent />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  )
}

interface PlanProps {
  name: string;
  price: string;
  period: string;
  description: string;
}

interface PricingCardProps {
  plan: PlanProps;
  featured?: boolean;
}

interface FeatureCardProps {
  title: string;
  description: string;
  icon: LucideIcon;
  mockupImage: string;
}

const PricingCard: React.FC<PricingCardProps> = ({ plan, featured = false }) => {
  const { t } = useTranslation();
  return (
    <div className={`flex flex-col p-8 rounded-[2rem] ${featured ? 'panel-dark ring-0' : 'panel-tint-blue'}`}>
      <div className="mb-8">
        <h3 className={`text-xl font-bold ${featured ? 'text-white' : ''}`}>{plan.name}</h3>
        <p className={`mt-2 text-sm ${featured ? 'text-white/60' : 'text-muted-foreground'}`}>{plan.description}</p>
        <div className="mt-4">
          <span className={`text-3xl font-black tracking-tight ${featured ? 'text-white' : ''}`}>${plan.price}</span>
          <span className={featured ? 'text-white/60' : 'text-muted-foreground'}>/{plan.period}</span>
        </div>
      </div>

      <button
        className={`mt-auto rounded-full px-8 py-3 font-medium ${
          featured ? 'bg-white text-zinc-900 hover:bg-white/90' : 'bg-zinc-900 text-white hover:bg-zinc-800'
        }`}
        onClick={() => window.open('https://apps.apple.com/us/app/currents-diving-app/id6504115639', '_blank')}
      >
        {t('pro.plans.cta')}
      </button>
    </div>
  )
}

const FeatureCard: React.FC<FeatureCardProps & { tint: string }> = ({ title, description, icon: Icon, mockupImage, tint }) => {
  return (
    <div className={`${tint} rounded-[2rem] overflow-hidden`}>
      <div className="flex flex-col lg:flex-row h-full p-6 sm:p-8 lg:p-10 gap-6 lg:gap-8">
        <div className="flex-1 space-y-4 sm:space-y-6 w-full lg:w-1/2">
          <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center shadow-sm">
            <Icon className="w-6 h-6 text-[#1F80FF]" />
          </div>
          <h3 className="text-xl sm:text-2xl font-black tracking-tight">{title}</h3>
          <p className="text-base sm:text-lg text-muted-foreground leading-relaxed">
            {description}
          </p>
        </div>
        <div className="flex-1 flex items-center justify-center w-full lg:w-1/2">
          <img
            src={mockupImage}
            alt={title}
            className="w-auto h-[250px] sm:h-[350px] lg:h-[400px] object-contain drop-shadow-xl"
          />
        </div>
      </div>
    </div>
  )
}

const ProPage: React.FC = () => {
  const { t } = useTranslation();
  const features: FeatureCardProps[] = [
    {
      title: t('pro.features.import.title'),
      description: t('pro.features.import.description'),
      icon: FileDown,
      mockupImage: "/currents-app-sync-computer.png"
    },
    {
      title: t('pro.features.insights.title'),
      description: t('pro.features.insights.description'),
      icon: Compass,
      mockupImage: "/currents-app-depth-metrics.png"
    },
    {
      title: t('pro.features.reviewImport.title'),
      description: t('pro.features.reviewImport.description'),
      icon: ListChecks,
      mockupImage: "/currents-app-review-import.png"
    },
    {
      title: t('pro.features.diveTools.title'),
      description: t('pro.features.diveTools.description'),
      icon: Calculator,
      mockupImage: "/currents-app-dive-tools.png"
    },
    {
      title: t('pro.features.dataControl.title'),
      description: t('pro.features.dataControl.description'),
      icon: Award,
      mockupImage: "/currents-app-pdf-export.png"
    }
  ];

  return (
    <div className="flex min-h-screen flex-col relative">
      <SEO 
        path="/pro"
      />
      <Header />
      <main className="flex-1">
        {/* Hero Section */}
        <section className="px-3 pt-3">
          <div className="panel-hero rounded-[2.5rem]">
          <div className="container flex flex-col items-center justify-center space-y-5 pt-40 md:pt-48 pb-16 text-center">
            <div className="inline-block rounded-full bg-white px-3 py-1 text-sm font-semibold text-[#1F80FF] shadow-sm">
              {t('pro.hero.badge.label')} <span className="ml-1 bg-[#1F80FF] text-white px-2 py-0.5 rounded-full text-xs font-bold">{t('pro.hero.badge.tag')}</span>
            </div>
            <h1 className="text-4xl md:text-6xl font-black tracking-tighter leading-[0.95]">
              {t('pro.hero.titleLine1')}<br />{t('pro.hero.titleLine2')}
            </h1>
            <p className="max-w-[600px] text-lg md:text-xl text-muted-foreground">
              {t('pro.hero.subtitle')}
            </p>
          </div>
          </div>
        </section>

        {/* Updated Pricing Section */}
        <section className="container py-20 z-20">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-black tracking-tight">{t('pro.pricing.title')}</h2>
            <p className="mt-4 text-lg text-muted-foreground">
              {t('pro.pricing.subtitle')}
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-[1200px] mx-auto">
            <PricingCard
              plan={{
                name: t('pro.plans.monthly.name'),
                price: "2.99",
                period: "month",
                description: t('pro.plans.monthly.description')
              }}
            />
            <PricingCard
              plan={{
                name: t('pro.plans.yearly.name'),
                price: "19.99",
                period: "year",
                description: t('pro.plans.yearly.description')
              }}
              featured={true}
            />
            <PricingCard
              plan={{
                name: t('pro.plans.lifetime.name'),
                price: "29.99",
                period: "one-time",
                description: t('pro.plans.lifetime.description')
              }}
            />
          </div>
          <p className="text-sm text-muted-foreground text-center mt-8">
            {t('pro.priceDisclaimer')}
          </p>
        </section>

        <FreeVsProSection />

        {/* Features Grid Section */}
        <section className="container py-12 sm:py-16 lg:py-20 z-20">
          <div className="text-center mb-8 sm:mb-12 lg:mb-16">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-black tracking-tight">{t('pro.featuresSection.title')}</h2>
            <p className="mt-4 text-base sm:text-lg text-muted-foreground">
              {t('pro.featuresSection.subtitle')}
            </p>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 lg:gap-8 max-w-[1200px] mx-auto px-2 sm:px-4">
            {features.map((feature, index) => (
              <FeatureCard key={index} {...feature} tint={['panel-tint-blue', 'panel-tint-lavender', 'panel-tint-mint', 'panel-tint-blue'][index % 4]} />
            ))}
          </div>
        </section>
      </main>
      <GradientBackground />
      <Footer />
      <Analytics />
    </div>
  )
}

export default ProPage